/**
 * Accessible names that `@munifocus/ui` primitives need but cannot translate.
 *
 * Some ARIA roles are only conformant with an accessible name — a
 * `role="progressbar"` with no name is a WCAG 1.1.1 failure — but the name has
 * to come from somewhere. Threading a label through all ~160 `CircularProgress`
 * call sites is not maintainable, and this library has no i18n dependency by
 * design (see the command-palette note: each app resolves its own strings).
 *
 * So the labels live in one context, defaulted to English, and each app feeds
 * it translated values from `AppThemeProvider`. Apps that pass nothing still
 * get a conformant name — just an untranslated one.
 */
import { createContext, useContext } from 'react';

export interface A11yLabels {
  /** Names indeterminate progress indicators. `common.a11y.loading`. */
  loading: string;
}

export const DEFAULT_A11Y_LABELS: A11yLabels = {
  loading: 'Loading',
};

const A11yLabelsContext = createContext<A11yLabels>(DEFAULT_A11Y_LABELS);

export const A11yLabelsProvider = A11yLabelsContext.Provider;

export function useA11yLabels(): A11yLabels {
  return useContext(A11yLabelsContext);
}
